import React,{useState,useMemo,useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {PageHeader,FilterBar,DataTable,Badge,EmptyState,FormSection,Modal} from '../components/ui/SharedUI';
import {demoRepository} from '../repository/demoRepository';
import {calculations} from '../utils/calculations';
import {useFeedback} from '../components/ui/FeedbackUI';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const formatPdfCurrency=(value)=> calculations.formatCurrencyAUD(value);
const formatPdfPercent=(value)=> calculations.formatPercentage(value,false);
const formatPdfDate=(ts)=> ts ? calculations.formatDateAU(ts) : '-';
const safeText=(v)=> v || '-';
const normaliseStatus=(value)=> String(value || '').trim().toLowerCase().replace(/\s+/g,'_');
const getTransactionFinancialYear=(transaction)=> transaction?.financialYear || (transaction?.date ? calculations.getAustralianFinancialYear(transaction.date) : '');
const buildPreviousFinancialYears=(count=15)=> {
  const currentFy=calculations.getAustralianFinancialYear(new Date());
  const currentStartYear=Number(String(currentFy).split('-')[0]);
  if (!Number.isFinite(currentStartYear)) return [];
  return Array.from({length:count},(_,index)=> {
    const startYear=currentStartYear-index-1;
    return `${startYear}-${String(startYear+1).slice(-2)}`;
  });
};
const HISTORICAL_FY_OPTIONS=buildPreviousFinancialYears(15);

const addPdfHeader=(doc,title,filters)=> {
  const pageW=doc.internal.pageSize.getWidth();
  doc.setFillColor(15,23,42);
  doc.rect(0,0,pageW,38,'F');
  doc.setTextColor(245,158,11);
  doc.setFontSize(18);
  doc.text("Malhotra's Property Portfolio",14,14);
  doc.setTextColor(255,255,255);
  doc.setFontSize(12);
  doc.text(title,14,24);
  doc.setFontSize(9);
  doc.setTextColor(200,200,200);
  const genDate=new Date().toLocaleString('en-AU',{dateStyle:'medium',timeStyle:'short'});
  doc.text(`Generated: ${genDate}`,14,32);
  let filterText='';
  if (filters) {
    const parts=[];
    if (filters.propertyId && filters.propertyId !=='All') {
      const p=demoRepository.getPropertyById(filters.propertyId);
      parts.push(`Property: ${p?.name || filters.propertyId}`);
    }
    if (filters.financialYear && filters.financialYear !=='All') parts.push(`FY: ${filters.financialYear}`);
    if (filters.includeArchived) parts.push('Include Historical');
    filterText=parts.join(' | ');
  }
  if (filterText) doc.text(`Filters: ${filterText}`,pageW-14,32,{align:'right'});
  doc.setDrawColor(245,158,11);
  doc.setLineWidth(0.5);
  doc.line(14,38,pageW-14,38);
};

const addPdfFooter=(doc)=> {
  const pageH=doc.internal.pageSize.getHeight();
  const pageW=doc.internal.pageSize.getWidth();
  const totalPages=doc.internal.getNumberOfPages();
  for (let i=1;i<=totalPages;i++) {
    doc.setPage(i);
    doc.setFillColor(15,23,42);
    doc.rect(0,pageH-18,pageW,18,'F');
    doc.setTextColor(200,200,200);
    doc.setFontSize(8);
    doc.text("Generated by Malhotra's Property Portfolio",14,pageH-7);
    doc.text(`Page ${i} of ${totalPages}`,pageW-14,pageH-7,{align:'right'});
  }
};

const ReportsPage=()=> {
  const navigate=useNavigate();
  const {showToast,showStagedMessages}=useFeedback();
  const [selectedReport,setSelectedReport]=useState(null);
  const [filters,setFilters]=useState({
    propertyId:'All',
    financialYear:calculations.getAustralianFinancialYear(new Date()),
    startDate:null,
    endDate:null,
    includeArchived:false
  });
  const selectedFinancialYear=filters.financialYear !=='All'
    ? filters.financialYear
    : calculations.getAustralianFinancialYear(new Date());

  const properties=useMemo(()=> {
    const all=demoRepository.getAllProperties();
    if (filters.includeArchived) return all;
    return all.filter(p=> !['archived','sold'].includes(normaliseStatus(p.status)));
  },[filters.includeArchived]);

  const availableFinancialYears=useMemo(()=> {
    const years=new Set([
      calculations.getAustralianFinancialYear(new Date()),
      ...HISTORICAL_FY_OPTIONS,
      ...demoRepository.getTransactions().map(getTransactionFinancialYear).filter(Boolean)
    ]);
    return [...years].sort((a,b)=> Number(String(b).split('-')[0])-Number(String(a).split('-')[0]));
  },[]);

  const filteredTransactions=useMemo(()=> {
    let txns=demoRepository.getTransactions();
    if (filters.propertyId !=='All') txns=txns.filter(t=> t.propertyId===filters.propertyId);
    if (filters.financialYear !=='All') txns=txns.filter(t=> getTransactionFinancialYear(t)===filters.financialYear);
    if (!filters.includeArchived) txns=txns.filter(t=> normaliseStatus(t.status)!=='archived');
    return txns;
  },[filters]);

  const scopedPropertyIds=useMemo(()=> {
    if (filters.propertyId !=='All') return [filters.propertyId];
    if (filters.includeArchived) return properties.map(property=> property.id);
    return null;
  },[filters.propertyId,filters.includeArchived,properties]);

  const portfolioSummary=useMemo(()=> calculations.getPortfolioSummary(scopedPropertyIds,selectedFinancialYear),[scopedPropertyIds,selectedFinancialYear]);

  const reportProperties=useMemo(()=> {
    const includedIds=new Set((portfolioSummary.includedPropertyIds||[]).map(String));
    return properties.filter(p=> includedIds.has(String(p.id)));
  },[properties,portfolioSummary.includedPropertyIds]);

  const taxSummary=useMemo(()=> {
    const fy=selectedFinancialYear;
    const txns=filteredTransactions.filter(t=> getTransactionFinancialYear(t)===fy && normaliseStatus(t.status)==='paid');
    const amount=(t)=> Math.abs(Number(t.amount)||0);
    const category=(t)=> calculations.normaliseTransactionCategory(t.category);
    const taxTreatment=(t)=> String(t.taxTreatment ?? t.taxCategory ?? '').trim().toLowerCase().replace(/[\s-]+/g,'_');
    const summaryMarker=(t)=> calculations.getHistoricalFySummaryMarker(t);
    const sumWhere=(predicate)=> calculations.roundMoney(txns.filter(predicate).reduce((sum,t)=> sum+amount(t),0));
    const agentFeeCategories=new Set(['agent_management_fee','letting_fee','renewal_fee','admin_fee','inspection_fee','marketing_fee']);
    const acquisitionCategories=new Set(['stamp_duty','legal_conveyancing','buyer_agent','building_pest','acquisition_cost']);
    const capitalCategories=new Set(['capital_improvement','capital_works','depreciation']);
    const borrowingCategories=new Set(['borrowing_cost','loan_fees','bank_fees']);
    const otherOperatingExpenseCategories=new Set(['utilities','cleaning','gardening']);
    const reviewCategories=new Set(['accountant_review','other_income','other_expense']);
    return {
      rentalIncome:sumWhere(t=> String(t.type||'').toLowerCase()==='income' && category(t)==='rent'),
      otherIncome:sumWhere(t=> String(t.type||'').toLowerCase()==='income' && category(t)!=='rent'),
      historicalAggregateIncome:sumWhere(t=> summaryMarker(t)===calculations.HISTORICAL_FY_SUMMARY_MARKERS.income),
      historicalAggregateOperatingExpenses:sumWhere(t=> summaryMarker(t)===calculations.HISTORICAL_FY_SUMMARY_MARKERS.operatingExpenses),
      agentDeductions:sumWhere(t=> agentFeeCategories.has(category(t))),
      councilRates:sumWhere(t=> category(t)==='council'),
      waterRates:sumWhere(t=> category(t)==='water'),
      strataFees:sumWhere(t=> category(t)==='strata'),
      repairs:sumWhere(t=> category(t)==='repairs' || category(t)==='maintenance'),
      insurance:sumWhere(t=> category(t)==='insurance'),
      otherOperatingExpenses:sumWhere(t=> otherOperatingExpenseCategories.has(category(t))),
      loanInterest:sumWhere(t=> category(t)==='loan_interest'),
      borrowingCosts:sumWhere(t=> borrowingCategories.has(category(t))),
      capitalImprovements:sumWhere(t=> capitalCategories.has(category(t))),
      acquisitionCosts:sumWhere(t=> acquisitionCategories.has(category(t))),
      privateNonDeductible:sumWhere(t=> category(t)==='private_non_deductible' || taxTreatment(t)==='private_non_deductible'),
      accountantReview:sumWhere(t=> reviewCategories.has(category(t)) || taxTreatment(t)==='accountant_review'),
      paidTransactionCount:txns.length
    };
  },[filteredTransactions,selectedFinancialYear]);

  const totalRecordedExpenseCategories=useMemo(()=> {
    return calculations.roundMoney(taxSummary.councilRates+taxSummary.waterRates+taxSummary.strataFees+taxSummary.repairs+taxSummary.insurance+taxSummary.otherOperatingExpenses+taxSummary.historicalAggregateOperatingExpenses+taxSummary.loanInterest+taxSummary.borrowingCosts+taxSummary.agentDeductions);
  },[taxSummary]);

  const reportCards=[
    {id:'portfolio',title:'Portfolio Summary',description:'High-level overview of total equity, yields, occupancy and selected-FY operating performance.',icon:'PieChart',available:true},
    {id:'property',title:'Property Report',description:'Detailed breakdown of a single property\'s performance, income, expenses, and metrics.',icon:'Home',available:true},
    {id:'tax',title:'Tax Preparation Summary',description:'Paid transaction categories for the selected Australian Financial Year, prepared for accountant review.',icon:'FileText',available:true},
    {id:'transactions',title:'Transaction Ledger',description:'Complete transaction history with filtering and export capabilities.',icon:'DollarSign',available:true},
    {id:'documents',title:'Document Register',description:'List of all active leases, insurances, and compliance records.',icon:'Folder',available:true},
    {id:'open-items',title:'Open Items Register',description:'Summary of pending maintenance and agent actions by property.',icon:'AlertCircle',available:true},
    {id:'insurance',title:'Insurance Report',description:'Policy summary with expiry tracking and premium analysis.',icon:'Shield',available:true},
    {id:'loans',title:'Loan Report',description:'Mortgage balances, interest rates, and loan-to-value ratios.',icon:'Briefcase',available:false}
  ];

  const handleExportCSV=useCallback((data,filename,headers)=> {
    showStagedMessages([
      'Preparing report data...','Calculating totals...','Formatting export...','Export ready!'
    ],()=> {
      const rows=data.map(row=> headers.map(h=> `"${row[h] || ''}"`).join(','));
      const csv=[headers.join(','),...rows].join('\n');
      const blob=new Blob([csv],{type:'text/csv'});
      const url=URL.createObjectURL(blob);
      const a=document.createElement('a');
      a.href=url;
      a.download=`${filename}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('CSV export downloaded','success');
    });
  },[showStagedMessages,showToast]);

  const handleDownloadPDF=useCallback((reportType)=> {
    try {
      switch (reportType) {
        case 'portfolio':generatePortfolioPDF();break;
        case 'property':generatePropertyPDF();break;
        case 'tax':generateTaxPDF();break;
        case 'transactions':generateTransactionsPDF();break;
        case 'documents':generateDocumentsPDF();break;
        case 'open-items':generateOpenItemsPDF();break;
        case 'insurance':generateInsurancePDF();break;
        default:showToast('Unknown report type','error');return;
      }
    } catch (error) {
      showToast(`PDF export failed: ${error?.message || 'Unknown error'}`,'error');
    }
  },[filters,portfolioSummary,reportProperties,taxSummary,totalRecordedExpenseCategories,filteredTransactions,selectedFinancialYear,showToast]);

  const generatePortfolioPDF=()=> {
    const doc=new jsPDF('l','mm','a4');
    addPdfHeader(doc,'Portfolio Summary Report',filters);
    let y=44;
    doc.setTextColor(30,30,30);
    doc.setFontSize(11);
    doc.setFont('helvetica','bold');
    doc.text('Portfolio KPIs',14,y);
    y+=6;

    const kpiRows=[
      ['Total Market Value',formatPdfCurrency(portfolioSummary.totalMarketValue)],
      ['Total Acquisition Cost',formatPdfCurrency(portfolioSummary.totalAcquisitionCost)],
      ['Total Equity',formatPdfCurrency(portfolioSummary.totalEquity)],
      ['Portfolio LVR',formatPdfPercent(portfolioSummary.portfolioLVR)+'%'],
      ['Annual Gross Rent (Expected)',formatPdfCurrency(portfolioSummary.annualRent)],
      [`Net Operating Income (FY ${portfolioSummary.currentFinancialYear})`,formatPdfCurrency(portfolioSummary.netOperatingIncome)],
      [portfolioSummary.netYieldComplete ? 'Net Yield (TTM)' : 'Net Yield (TTM · Provisional)',formatPdfPercent(portfolioSummary.netYield)+'%'],
      ['Occupancy Rate',formatPdfPercent(portfolioSummary.occupancyRate)+'%'],
      [`Cash Flow After Finance (FY ${portfolioSummary.currentFinancialYear})`,formatPdfCurrency(portfolioSummary.cashFlowAfterFinance)]
    ];

    autoTable(doc,{startY:y,head:[['Metric','Value']],body:kpiRows,theme:'plain',styles:{fontSize:9,cellPadding:3},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:80},1:{cellWidth:60,halign:'right'}}});

    y=doc.lastAutoTable.finalY+10;
    doc.setFontSize(11);
    doc.setFont('helvetica','bold');
    doc.text('Property Breakdown',14,y);
    y+=6;

    const propRows=reportProperties.map(p=> {
      const s=calculations.getPropertySummary(p.id,selectedFinancialYear);
      return [safeText(p.name),safeText(p.propertyType),formatPdfCurrency(p.currentMarketValue),formatPdfCurrency(s.totalAcquisitionCost),formatPdfCurrency(s.totalLoanBalance),formatPdfCurrency(s.equity),formatPdfPercent(s.lvr)+'%',formatPdfPercent(s.occupancyRate)+'%',formatPdfCurrency(s.cashFlowAfterFinance)];
    });

    autoTable(doc,{startY:y,head:[['Property','Type','Market Value','Acquisition','Loan Bal','Equity','LVR','Occupancy','Cash Flow After Finance']],body:propRows,theme:'striped',styles:{fontSize:8,cellPadding:2},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:40},1:{cellWidth:25},2:{cellWidth:25,halign:'right'},3:{cellWidth:25,halign:'right'},4:{cellWidth:25,halign:'right'},5:{cellWidth:25,halign:'right'},6:{cellWidth:18,halign:'right'},7:{cellWidth:18,halign:'right'},8:{cellWidth:25,halign:'right'}}});

    if (portfolioSummary.dataQualityIssues?.length>0) {
      y=doc.lastAutoTable.finalY+8;
      doc.setFontSize(8);
      doc.setTextColor(130,90,20);
      doc.text(`Calculation review: ${portfolioSummary.dataQualityIssues[0]}${portfolioSummary.dataQualityIssues.length>1 ? ` (+${portfolioSummary.dataQualityIssues.length-1} more)` : ''}`,14,y,{maxWidth:260});
    }

    addPdfFooter(doc);
    doc.save(`mpp-portfolio-report-${new Date().toISOString().split('T')[0]}.pdf`);
    showToast('Portfolio PDF downloaded','success');
  };

  const generatePropertyPDF=()=> {
    const propId=filters.propertyId !=='All' ? filters.propertyId : properties[0]?.id;
    if (!propId) {showToast('No property selected','error');return;}
    const prop=demoRepository.getPropertyById(propId);
    const summary=calculations.getPropertySummary(propId,selectedFinancialYear);
    const agent=prop.metadata?.agent||{};
    const doc=new jsPDF('p','mm','a4');
    addPdfHeader(doc,`Property Report: ${prop?.name || 'Unknown'}`,filters);
    let y=44;
    doc.setTextColor(30,30,30);
    doc.setFontSize(11);
    doc.setFont('helvetica','bold');
    doc.text('Property Details',14,y);
    y+=6;

    const detailRows=[
      ['Name',safeText(prop.name)],
      ['Address',safeText(prop.address)],
      ['Suburb',safeText(prop.suburb)],
      ['State',safeText(prop.state)],
      ['Postcode',safeText(prop.postcode)],
      ['Type',safeText(prop.propertyType)],
      ['Status',safeText(prop.status)],
      ['Purchase Price',formatPdfCurrency(prop.acquisitionPrice)],
      ['Market Value',formatPdfCurrency(prop.currentMarketValue)],
      ['Acquisition Cost',formatPdfCurrency(summary.totalAcquisitionCost)],
      ['Loan Balance',formatPdfCurrency(summary.totalLoanBalance)],
      ['Equity',formatPdfCurrency(summary.equity)],
      ['LVR',formatPdfPercent(summary.lvr)+'%'],
      ['Occupancy',formatPdfPercent(summary.occupancyRate)+'%']
    ];

    autoTable(doc,{startY:y,head:[['Field','Value']],body:detailRows,theme:'plain',styles:{fontSize:9,cellPadding:3},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:60},1:{cellWidth:80}}});

    if (agent.managerName || agent.agencyName) {
      y=doc.lastAutoTable.finalY+8;
      doc.setFontSize(11);
      doc.setFont('helvetica','bold');
      doc.text('Managing Agent',14,y);
      y+=6;
      const agentRows=[
        ['Agency',safeText(agent.agencyName)],
        ['Manager',safeText(agent.managerName)],
        ['Email',safeText(agent.email)],
        ['Phone',safeText(agent.phone)],
        ['Mgmt Fee',agent.managementFeePercent?`${agent.managementFeePercent}%`:'-']
      ];
      autoTable(doc,{startY:y,head:[['Field','Value']],body:agentRows,theme:'plain',styles:{fontSize:9,cellPadding:3},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:60},1:{cellWidth:80}}});
    }

    y=doc.lastAutoTable.finalY+8;
    doc.setFontSize(11);
    doc.setFont('helvetica','bold');
    doc.text('Income / Expense Summary',14,y);
    y+=6;

    const performanceRows=[
      ['Expected Annual Gross Rent',formatPdfCurrency(summary.annualRent)],
      [`Actual Operating Income (FY ${summary.currentFinancialYear})`,formatPdfCurrency(summary.actualOperatingIncome)],
      [`Actual Operating Expenses (FY ${summary.currentFinancialYear})`,formatPdfCurrency(summary.actualOperatingExpenses)],
      [`Net Operating Income (FY ${summary.currentFinancialYear})`,formatPdfCurrency(summary.netOperatingIncome)],
      [`Financing Cash Outflow (FY ${summary.currentFinancialYear})`,formatPdfCurrency(summary.financingCashOutflow)],
      [`Cash Flow After Finance (FY ${summary.currentFinancialYear})`,formatPdfCurrency(summary.cashFlowAfterFinance)],
      ['Gross Yield',formatPdfPercent(summary.grossYield)+'%'],
      [summary.netYieldComplete ? 'Net Yield (TTM)' : 'Net Yield (TTM · Provisional)',formatPdfPercent(summary.netYield)+'%']
    ];

    autoTable(doc,{startY:y,head:[['Category','Amount']],body:performanceRows,theme:'plain',styles:{fontSize:9,cellPadding:3},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:85},1:{cellWidth:55,halign:'right'}}});

    if (summary.dataQualityIssues?.length>0) {
      y=doc.lastAutoTable.finalY+8;
      doc.setFontSize(8);
      doc.setTextColor(130,90,20);
      doc.text(`Calculation review: ${summary.dataQualityIssues[0]}${summary.dataQualityIssues.length>1 ? ` (+${summary.dataQualityIssues.length-1} more)` : ''}`,14,y,{maxWidth:180});
    }

    addPdfFooter(doc);
    doc.save(`mpp-property-report-${(prop?.name || 'unknown').replace(/\s+/g,'-')}-${new Date().toISOString().split('T')[0]}.pdf`);
    showToast('Property PDF downloaded','success');
  };

  const generateTaxPDF=()=> {
    const doc=new jsPDF('p','mm','a4');
    const fy=selectedFinancialYear;
    const propName=filters.propertyId !=='All' ? (demoRepository.getPropertyById(filters.propertyId)?.name||'') : '';
    const headerTitle=propName ? `Tax Preparation Summary — ${propName}` : 'Tax Preparation Summary';
    addPdfHeader(doc,headerTitle,filters);
    let y=44;

    doc.setTextColor(30,30,30);
    doc.setFontSize(10);
    doc.text(`Financial Year: ${fy}`,14,y);
    if (propName) { doc.text(`Property: ${propName}`,14,y+5); y+=13; } else { y+=8; }

    doc.setFontSize(11);
    doc.setFont('helvetica','bold');
    doc.text('Recorded Paid Income',14,y);
    y+=6;

    autoTable(doc,{startY:y,head:[['Category','Amount']],body:[
      ['Rental Income',formatPdfCurrency(taxSummary.rentalIncome)],
      ['Other Income',formatPdfCurrency(taxSummary.otherIncome)],
      ['Total Recorded Income',formatPdfCurrency(taxSummary.rentalIncome+taxSummary.otherIncome)]
    ],theme:'plain',styles:{fontSize:9,cellPadding:3},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:80},1:{cellWidth:60,halign:'right'}}});

    y=doc.lastAutoTable.finalY+8;
    doc.setFont('helvetica','bold');
    doc.text('Recorded Expense Categories',14,y);
    y+=6;

    autoTable(doc,{startY:y,head:[['Expense','Amount']],body:[
      ['Agent Deductions',formatPdfCurrency(taxSummary.agentDeductions)],
      ['Council Rates',formatPdfCurrency(taxSummary.councilRates)],
      ['Water Rates',formatPdfCurrency(taxSummary.waterRates)],
      ['Strata Fees',formatPdfCurrency(taxSummary.strataFees)],
      ['Repairs & Maintenance',formatPdfCurrency(taxSummary.repairs)],
      ['Insurance Premiums',formatPdfCurrency(taxSummary.insurance)],
      ['Utilities / Cleaning / Gardening',formatPdfCurrency(taxSummary.otherOperatingExpenses)],
      ['Historical FY Aggregate Operating Expenses (Unitemised)',formatPdfCurrency(taxSummary.historicalAggregateOperatingExpenses)],
      ['Loan Interest',formatPdfCurrency(taxSummary.loanInterest)],
      ['Borrowing Costs / Loan Fees',formatPdfCurrency(taxSummary.borrowingCosts)],
      ['Total Recorded Expense Categories',formatPdfCurrency(totalRecordedExpenseCategories)]
    ],theme:'plain',styles:{fontSize:9,cellPadding:3},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:80},1:{cellWidth:60,halign:'right'}}});

    y=doc.lastAutoTable.finalY+8;
    doc.setFont('helvetica','bold');
    doc.text('Capital, Private & Review Items',14,y);
    y+=6;

    autoTable(doc,{startY:y,head:[['Item','Amount']],body:[
      ['Capital Improvements',formatPdfCurrency(taxSummary.capitalImprovements)],
      ['Acquisition Costs',formatPdfCurrency(taxSummary.acquisitionCosts)],
      ['Private / Non-Deductible',formatPdfCurrency(taxSummary.privateNonDeductible)],
      ['Accountant / Classification Review',formatPdfCurrency(taxSummary.accountantReview)]
    ],theme:'plain',styles:{fontSize:9,cellPadding:3},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:80},1:{cellWidth:60,halign:'right'}}});

    y=doc.lastAutoTable.finalY+8;
    doc.setFont('helvetica','bold');
    doc.setFontSize(11);
    doc.text('Preparation Summary',14,y);
    y+=6;

    const totalIncome=calculations.roundMoney(taxSummary.rentalIncome+taxSummary.otherIncome);
    const indicativeNetRentalPosition=calculations.roundMoney(totalIncome-totalRecordedExpenseCategories);

    autoTable(doc,{startY:y,head:[['','Amount']],body:[
      ['Total Recorded Paid Income',formatPdfCurrency(totalIncome)],
      ['Recorded Expense Categories',formatPdfCurrency(totalRecordedExpenseCategories)],
      ['Indicative Net Rental Position',formatPdfCurrency(indicativeNetRentalPosition)]
    ],theme:'plain',styles:{fontSize:9,cellPadding:3},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:80},1:{cellWidth:60,halign:'right'}}});

    y=doc.lastAutoTable.finalY+8;
    doc.setFontSize(8);
    doc.setTextColor(150,150,150);
    const aggregateNote=taxSummary.historicalAggregateOperatingExpenses>0
      ? ' Historical FY aggregate operating expenses are included in the indicative position but remain unitemised and require accountant classification.'
      : '';
    doc.text(`This is a record-preparation summary based on Paid transactions. It does not determine tax deductibility, timing, apportionment or taxable income; an accountant should confirm those treatments.${aggregateNote}`,14,y,{maxWidth:180});

    addPdfFooter(doc);
    const filename=propName
      ? `mpp-tax-preparation-${propName.replace(/\s+/g,'-')}-${fy}-${new Date().toISOString().split('T')[0]}.pdf`
      : `mpp-tax-preparation-${fy}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
    showToast('Tax preparation PDF downloaded','success');
  };

  const generateTransactionsPDF=()=> {
    const doc=new jsPDF('l','mm','a4');
    addPdfHeader(doc,'Transaction Ledger Report',filters);
    let y=44;

    const realisedTransactions=filteredTransactions.filter(t=> normaliseStatus(t.status)==='paid');
    const income=realisedTransactions.filter(t=> t.amount>0).reduce((s,t)=> s+t.amount,0);
    const expenses=realisedTransactions.filter(t=> t.amount<0).reduce((s,t)=> s+Math.abs(t.amount),0);
    const net=income-expenses;

    doc.setTextColor(30,30,30);
    doc.setFontSize(11);
    doc.setFont('helvetica','bold');
    doc.text('Realised Transaction Summary',14,y);
    y+=6;

    autoTable(doc,{startY:y,head:[['Metric','Amount']],body:[
      ['Paid Inflows',formatPdfCurrency(income)],
      ['Paid Outflows',formatPdfCurrency(expenses)],
      ['Net Paid Movement',formatPdfCurrency(net)]
    ],theme:'plain',styles:{fontSize:9,cellPadding:3},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:80},1:{cellWidth:60,halign:'right'}}});

    y=doc.lastAutoTable.finalY+8;
    doc.setFont('helvetica','bold');
    doc.text('Transaction Detail',14,y);
    y+=6;

    const txnRows=filteredTransactions.slice(0,100).map(t=> {
      const prop=demoRepository.getPropertyById(t.propertyId);
      return [formatPdfDate(t.date),safeText(t.type),safeText(t.category),safeText(prop?.name),safeText(t.description),formatPdfCurrency(t.amount),safeText(t.status),safeText(getTransactionFinancialYear(t))];
    });

    const note=filteredTransactions.length>100 ? `Showing first 100 of ${filteredTransactions.length} transactions.` : '';

    autoTable(doc,{startY:y,head:[['Date','Type','Category','Property','Description','Amount','Status','FY']],body:txnRows,theme:'striped',styles:{fontSize:7,cellPadding:2},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:22},1:{cellWidth:18},2:{cellWidth:25},3:{cellWidth:30},4:{cellWidth:50},5:{cellWidth:22,halign:'right'},6:{cellWidth:20},7:{cellWidth:18}}});

    if (note) {
      const finalY=doc.lastAutoTable.finalY+4;
      doc.setFontSize(8);
      doc.setTextColor(150,150,150);
      doc.text(note,14,finalY);
    }

    addPdfFooter(doc);
    doc.save(`mpp-transactions-${new Date().toISOString().split('T')[0]}.pdf`);
    showToast('Transactions PDF downloaded','success');
  };

  const generateDocumentsPDF=()=> {
    const docs=filters.propertyId !=='All' ? demoRepository.getDocumentsByProperty(filters.propertyId) : demoRepository.getDocuments();
    const doc=new jsPDF('l','mm','a4');
    addPdfHeader(doc,'Document Register Report',filters);
    let y=44;

    const rows=docs.map(d=> {
      const prop=demoRepository.getPropertyById(d.propertyId);
      const fileCount=d.fileCount || d.files?.length || 0;
      return [safeText(d.title || d.fileName),safeText(d.documentType),safeText(prop?.name),formatPdfDate(d.uploadedDate || d.documentDate),safeText(d.financialYear),fileCount>0 ? `${fileCount} file(s)` : 'Metadata only',safeText(d.status)];
    });

    autoTable(doc,{startY:y,head:[['Title','Type','Property','Date','FY','Files','Status']],body:rows,theme:'striped',styles:{fontSize:8,cellPadding:2},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:40},1:{cellWidth:25},2:{cellWidth:30},3:{cellWidth:22},4:{cellWidth:18},5:{cellWidth:20},6:{cellWidth:18}}});

    addPdfFooter(doc);
    doc.save(`mpp-documents-${new Date().toISOString().split('T')[0]}.pdf`);
    showToast('Documents PDF downloaded','success');
  };

  const generateOpenItemsPDF=()=> {
    const openItems=demoRepository.getAllOpenItems().filter(oi=> {
      if (filters.propertyId !=='All' && String(oi.propertyId || '')!==String(filters.propertyId)) return false;
      if (!filters.includeArchived) {
        const status=normaliseStatus(oi.status);
        return status!=='archived' && status!=='resolved' && status!=='closed';
      }
      return true;
    });
    const doc=new jsPDF('l','mm','a4');
    addPdfHeader(doc,'Open Items Register Report',filters);
    let y=44;

    const rows=openItems.map(oi=> {
      const prop=demoRepository.getPropertyById(oi.propertyId);
      const timeline=oi.metadata?.timeline || [];
      const latestNote=timeline.length>0 ? timeline[0].text : '';
      return [safeText(prop?.name),safeText(oi.title),safeText(oi.priority),safeText(oi.status),safeText(oi.responsibleParty),formatPdfDate(oi.dueDate || oi.followUpDate),latestNote.length>60 ? latestNote.substring(0,60)+'...' : safeText(latestNote)];
    });

    autoTable(doc,{startY:y,head:[['Property','Title','Priority','Status','Responsible','Due Date','Latest Note']],body:rows,theme:'striped',styles:{fontSize:8,cellPadding:2},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:30},1:{cellWidth:35},2:{cellWidth:18},3:{cellWidth:20},4:{cellWidth:22},5:{cellWidth:22},6:{cellWidth:60}}});

    addPdfFooter(doc);
    doc.save(`mpp-open-items-${new Date().toISOString().split('T')[0]}.pdf`);
    showToast('Open Items PDF downloaded','success');
  };

  const generateInsurancePDF=()=> {
    const policies=filters.propertyId !=='All' ? demoRepository.getInsuranceByProperty(filters.propertyId) : demoRepository.getAllInsurancePolicies();
    const doc=new jsPDF('l','mm','a4');
    addPdfHeader(doc,'Insurance Report',filters);
    let y=44;

    const rows=policies.map(ins=> {
      const prop=demoRepository.getPropertyById(ins.propertyId);
      const daysToExpiry=calculations.calculateDaysToExpiry(ins.expiryDate);
      const expiryNote=daysToExpiry>0 ? `${daysToExpiry} days` : daysToExpiry===0 ? 'Today' : 'Expired';
      return [safeText(prop?.name),safeText(ins.provider),safeText(ins.policyNumber),safeText(ins.policyType),formatPdfCurrency(ins.premium),formatPdfCurrency(Number(ins.coverage||0)),formatPdfDate(ins.startDate),formatPdfDate(ins.expiryDate),expiryNote,safeText(ins.status)];
    });

    autoTable(doc,{startY:y,head:[['Property','Provider','Policy #','Type','Premium','Coverage','Start','Expiry','Days','Status']],body:rows,theme:'striped',styles:{fontSize:7,cellPadding:2},headStyles:{fillColor:[15,23,42],textColor:[245,158,11]},columnStyles:{0:{cellWidth:30},1:{cellWidth:25},2:{cellWidth:22},3:{cellWidth:18},4:{cellWidth:22,halign:'right'},5:{cellWidth:22,halign:'right'},6:{cellWidth:22},7:{cellWidth:22},8:{cellWidth:16},9:{cellWidth:16}}});

    addPdfFooter(doc);
    doc.save(`mpp-insurance-${new Date().toISOString().split('T')[0]}.pdf`);
    showToast('Insurance PDF downloaded','success');
  };

  const renderReportPreview=(title,description,type)=> (
    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">{title}</h2>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        <button onClick={()=> handleDownloadPDF(type)} className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg flex items-center gap-1.5 shrink-0">
          <SafeIcon icon="Download" className="text-base" /> Download PDF
        </button>
      </div>
      <div className="text-center py-12 bg-slate-50 rounded-lg border border-slate-200 border-dashed">
        <SafeIcon icon="FileText" className="text-4xl text-slate-300 mx-auto mb-3" />
        <h3 className="text-sm font-medium text-slate-700">Report Ready</h3>
        <p className="text-xs text-slate-500 mt-1">Click the Download PDF button to generate your professional report.</p>
        <p className="text-xs text-slate-400 mt-2">Active filters will be applied automatically.</p>
      </div>
    </div>
  );

  const renderPortfolioReport=()=> renderReportPreview('Portfolio Summary Report',`Authoritative portfolio totals with FY ${selectedFinancialYear} NOI/cash flow and trailing-12-month net yield.`,'portfolio');
  const renderTaxReport=()=> renderReportPreview('Tax Preparation Summary',`Paid transaction categories for Australian Financial Year ${selectedFinancialYear}, prepared for accountant review.`,'tax');
  const renderDocumentRegister=()=> renderReportPreview('Document Register Report','List of all active leases, insurances, and compliance records.','documents');
  const renderOpenItemsReport=()=> renderReportPreview('Open Items Register Report','Summary of pending maintenance and agent actions.','open-items');
  const renderInsuranceReport=()=> renderReportPreview('Insurance Report','Policy summary with expiry tracking and premium analysis.','insurance');

  return (
    <div className="animate-fade-in">
      <PageHeader title="Reports" subtitle="Generate standard reports for your properties and financials." />
      <FormSection title="Report Filters" className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Property</label>
            <select value={filters.propertyId} onChange={(e)=> setFilters(prev=> ({...prev,propertyId:e.target.value}))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500">
              <option value="All">All Properties</option>
              {properties.map(p=> <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Financial Year</label>
            <select value={filters.financialYear} onChange={(e)=> setFilters(prev=> ({...prev,financialYear:e.target.value}))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500">
              <option value="All">Current FY for performance / all years for registers</option>
              {availableFinancialYears.map(financialYear=> <option key={financialYear} value={financialYear}>{financialYear}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={filters.includeArchived} onChange={(e)=> setFilters(prev=> ({...prev,includeArchived:e.target.checked}))} className="rounded border-slate-300 text-amber-600 focus:ring-amber-500" />
              Include Historical
            </label>
          </div>
        </div>
      </FormSection>

      {!selectedReport ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reportCards.map((card)=> (
            <div key={card.id} onClick={()=> card.available && setSelectedReport(card.id)} className={`bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer group ${!card.available ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <div className="w-12 h-12 bg-slate-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-amber-50 transition-colors">
                <SafeIcon icon={card.icon} className="text-xl text-slate-600 group-hover:text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">{card.title}</h3>
              <p className="text-sm text-slate-500">{card.description}</p>
              {!card.available && (
                <Badge label="Coming Soon" status="default" />
              )}
              {card.id==='tax' && card.available && (
                <button
                  onClick={(e)=> {e.stopPropagation();handleDownloadPDF('tax');}}
                  className="mt-3 px-3 py-1.5 text-xs font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg flex items-center gap-1"
                >
                  <SafeIcon icon="Download" className="text-sm" /> Download PDF
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          <button onClick={()=> setSelectedReport(null)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
            <SafeIcon icon="ArrowLeft" /> Back to Reports
          </button>
          {selectedReport==='portfolio' && renderPortfolioReport()}
          {selectedReport==='tax' && renderTaxReport()}
          {selectedReport==='documents' && renderDocumentRegister()}
          {selectedReport==='open-items' && renderOpenItemsReport()}
          {selectedReport==='insurance' && renderInsuranceReport()}
          {selectedReport==='property' && (
            filters.propertyId !=='All' ?
              renderReportPreview('Property Report','Detailed breakdown of a single property\'s performance, income, expenses, and metrics.','property') :
              <EmptyState title="Property Report" description="Select a property from the filters above to view the detailed property report." icon="Home" />
          )}
          {selectedReport==='transactions' && (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
              <SafeIcon icon="DollarSign" className="text-4xl text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Transaction Ledger</h3>
              <p className="text-sm text-slate-600 mb-6">The transaction ledger is available directly on the Transactions page with full filtering and export capabilities.</p>
              <div className="flex justify-center gap-3">
                <button onClick={()=> navigate('/transactions')} className="px-4 py-2 bg-slate-900 text-white font-medium text-sm rounded-lg hover:bg-slate-800">
                  Go to Transactions
                </button>
                <button onClick={()=> handleDownloadPDF('transactions')} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 flex items-center gap-1.5">
                  <SafeIcon icon="Download" /> Download PDF Anyway
                </button>
              </div>
            </div>
          )}
          {selectedReport==='loans' && (
            <EmptyState title="Loan Report" description="This report will be available in a future phase with loan tracking functionality." icon="Briefcase" />
          )}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;